import React, { useEffect, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { useFormik } from 'formik';
import { useMutation, useQueryClient } from 'react-query';
import { _GoogleAddress, _ToastMessageProps } from '../../../../../constants/staticTypes';
import {
    ADD_NEW_VENUE,
    CANCEL,
    CHECKING_DOMAIN_AVAILABILITY,
    CITY,
    CONTACT_ADDRESS,
    CONTACT_NAME,
    COUNTRY,
    CREATE_FIRST_AVENUE_WARNING,
    EDIT_VENUE,
    EMAIL,
    PREFERRED_VENUE_URL,
    PRIMARY_PHONE,
    SAVE,
    SECONDARY_PHONE,
    SELECT_FROM_MAP,
    STATE,
    TIME_ZONE,
    UPDATE,
    VENUE_NAME,
    VENUE_URL,
    ZIP,
} from '../../../../../constants/strings';
import HttpServiceHelper from '../../../../../helpers/httpServiceHelper';
import { useTranslation } from '../../../../../hooks/useTranslation';
import { defaultToastData, initialAddress } from '../../../../../helpers/commonHelper';
import { AlertVariant, DOMAIN_SUFFIX } from '../../../../../constants/constants';
import MlxButton from '../../../../../components/common/MlxButton/MlxButton';
import MlxSideBar from '../../../../../components/common/MlxSideBar/MlxSideBar';
import MlxToast from '../../../../../components/common/MlxToast/MlxToast';
import {
    _VenueFormType,
    addNewVenue,
    addVenueValidationSchema,
    domainRefAddVenue,
    editVenue,
    editVenueValidationSchema,
    initialVenueFormDataValue,
    invalidateVenueOptions,
} from '../../../../../helpers/venueHelper';
import '../VenueListManagement.scss';
import MlxGoogleMaps from '../../../../../components/common/MlxGoogleMaps/MlxGoogleMaps';
import { useDebounce } from 'primereact/hooks';
import MlxCountryPhoneInput from '../../../../../components/common/MlxCountryPhoneInput/MlxCountryPhoneInput';

type Props = {
    showSideBar: boolean;
    setShowSideBar: React.Dispatch<React.SetStateAction<boolean>>;
    isVenueEdit?: boolean;
    setIsVenueEdit?: React.Dispatch<React.SetStateAction<boolean>>;
    venueEditData?: _VenueFormType;
    setVenueAdded: React.Dispatch<React.SetStateAction<boolean>>;
};

const languageTexts = {
    ADD_NEW_VENUE: ADD_NEW_VENUE,
    VENUE_NAME: VENUE_NAME,
    CONTACT_NAME: CONTACT_NAME,
    CONTACT_ADDRESS: CONTACT_ADDRESS,
    EMAIL: EMAIL,
    TIME_ZONE: TIME_ZONE,
    PRIMARY_PHONE: PRIMARY_PHONE,
    SECONDARY_PHONE: SECONDARY_PHONE,
    ZIP: ZIP,
    CITY: CITY,
    STATE: STATE,
    COUNTRY: COUNTRY,
    CREATE_FIRST_AVENUE_WARNING: CREATE_FIRST_AVENUE_WARNING,
    PREFERRED_VENUE_URL: PREFERRED_VENUE_URL,
    SELECT_FROM_MAP: SELECT_FROM_MAP,
    CANCEL: CANCEL,
    SAVE: SAVE,
    CHECKING_DOMAIN_AVAILABILITY: CHECKING_DOMAIN_AVAILABILITY,
    UPDATE: UPDATE,
    EDIT_VENUE: EDIT_VENUE,
    VENUE_URL: VENUE_URL,
};

const AddVenueSidebar = ({ showSideBar, setShowSideBar, isVenueEdit, setIsVenueEdit, venueEditData, setVenueAdded }: Readonly<Props>) => {
    const addVenueMutation = useMutation(HttpServiceHelper);
    const queryClient = useQueryClient();
    const translatedText = useTranslation(languageTexts, 'capitalize');
    const domainSuffix = DOMAIN_SUFFIX;

    const [toastMessage, setToastMessage] = useState<_ToastMessageProps>(defaultToastData);
    const [locationFormData, setLocationFormData] = useState<_GoogleAddress>(initialAddress);

    const [domainName, debouncedDomainName, setDomainName] = useDebounce('', 1000);

    const onSuccess = (data: any) => {
        setToastMessage({
            message: data?.message ?? '',
            variant: AlertVariant.SUCCESS,
            show: true,
        });
        onSidebarClose();
        invalidateVenueOptions(queryClient);
        setVenueAdded(true); // for list refetching
    };

    const onError = (error: string, variant: string) => {
        setToastMessage({
            message: error,
            variant,
            show: true,
        });
        onSidebarClose();
    };

    // Formik setup
    const venueFormikForm = useFormik({
        initialValues: initialVenueFormDataValue,
        enableReinitialize: true,
        validationSchema: isVenueEdit ? editVenueValidationSchema : addVenueValidationSchema,
        onSubmit: (values) => {
            if (isVenueEdit) {
                editVenue(addVenueMutation, values, onSuccess, onError);
            } else {
                addNewVenue(addVenueMutation, values, onSuccess, onError);
            }
        },
    });

    useEffect(() => {
        if (venueEditData) {
            venueFormikForm.setValues(venueEditData);
        }
    }, [venueEditData]);

    const onSidebarClose = () => {
        setShowSideBar(false);
        venueFormikForm.resetForm();
        setDomainName('');
        setLocationFormData(initialAddress);
        if (setIsVenueEdit) setIsVenueEdit(false);
    };

    const addressCallback = (address: _GoogleAddress) => {
        setLocationFormData(address);

        venueFormikForm.setFieldValue('address', address?.formatted_address ?? '');
        venueFormikForm.setFieldValue('zip', address?.zip_code ?? '');
        venueFormikForm.setFieldValue('city', address?.city_town ?? '');
        venueFormikForm.setFieldValue('state', address?.state ?? '');
        venueFormikForm.setFieldValue('country', address?.country_region ?? '');
    };

    useEffect(() => {
        if (debouncedDomainName) {
            venueFormikForm.setFieldTouched('domainName', true);
        }
        venueFormikForm.setFieldValue('domainName', debouncedDomainName, true);
    }, [debouncedDomainName]);

    return (
        <>
            <MlxToast
                data-testid="toast"
                message={toastMessage.message}
                onClose={() => setToastMessage(defaultToastData)}
                show={toastMessage.show}
                variant={toastMessage.variant}
            />
            <MlxSideBar
                visible={showSideBar}
                position="right"
                onHide={() => onSidebarClose()}
                content={({ hide }) => (
                    <>
                        <div className="mlx-sidebar-header">
                            <span className="header-title">{isVenueEdit ? translatedText.EDIT_VENUE : translatedText.ADD_NEW_VENUE}</span>
                            <MlxButton
                                rounded
                                text
                                size="large"
                                icon="ri-close-fill"
                                onClick={(event) => hide(event)}
                                className="text-dark"
                            />
                        </div>

                        <form onSubmit={venueFormikForm.handleSubmit} className="mlx-sidebar-body add-staff-form">
                            <div className="mb-3 d-flex flex-column">
                                <label className="mb-1" htmlFor="tenantName">
                                    {translatedText.VENUE_NAME} <span className="text-danger">*</span>
                                </label>
                                <InputText
                                    id="tenantName"
                                    name="tenantName"
                                    className={`custom-input-text p-inputtext-sm ${venueFormikForm.touched.tenantName && venueFormikForm.errors.tenantName ? 'border-danger' : ''}`}
                                    value={venueFormikForm.values.tenantName}
                                    onChange={venueFormikForm.handleChange}
                                    onBlur={venueFormikForm.handleBlur}
                                    autoComplete="off"
                                />
                                {venueFormikForm.touched.tenantName && venueFormikForm.errors.tenantName && (
                                    <div className="text-danger">{venueFormikForm.errors.tenantName}</div>
                                )}
                            </div>

                            <div className="mb-3 d-flex flex-column">
                                <label className="mb-1" htmlFor="contactName">
                                    {translatedText.CONTACT_NAME} <span className="text-danger">*</span>
                                </label>
                                <InputText
                                    id="contactName"
                                    name="contactName"
                                    className={`custom-input-text p-inputtext-sm ${venueFormikForm.touched.contactName && venueFormikForm.errors.contactName ? 'border-danger' : ''}`}
                                    value={venueFormikForm.values.contactName}
                                    onChange={venueFormikForm.handleChange}
                                    onBlur={venueFormikForm.handleBlur}
                                    autoComplete="off"
                                />
                                {venueFormikForm.touched.contactName && venueFormikForm.errors.contactName && (
                                    <div className="text-danger">{venueFormikForm.errors.contactName}</div>
                                )}
                            </div>

                            <div className="mb-3 d-flex flex-column">
                                <label className="mb-1" htmlFor="email">
                                    {translatedText.EMAIL} <span className="text-danger">*</span>
                                </label>
                                <InputText
                                    id="email"
                                    name="email"
                                    className={`custom-input-text p-inputtext-sm ${venueFormikForm.touched.email && venueFormikForm.errors.email ? 'border-danger' : ''}`}
                                    value={venueFormikForm.values.email}
                                    onChange={venueFormikForm.handleChange}
                                    onBlur={venueFormikForm.handleBlur}
                                    autoComplete="off"
                                />
                                {venueFormikForm.touched.email && venueFormikForm.errors.email && (
                                    <small className="text-danger">{venueFormikForm.errors.email}</small>
                                )}
                            </div>

                            <div className="row">
                                <div className="col-md-6 mb-3 d-flex flex-column">
                                    <label className="mb-1" htmlFor="primaryPhone">
                                        {translatedText.PRIMARY_PHONE}
                                    </label>
                                    <MlxCountryPhoneInput
                                        id="primaryPhone"
                                        defaultCountry="US"
                                        value={venueFormikForm.values.primaryPhone}
                                        className="custom-input-text p-inputtext-sm"
                                        onChange={(value) => {
                                            venueFormikForm.setFieldTouched('primaryPhone', true);
                                            venueFormikForm.setFieldValue('primaryPhone', value, true);
                                        }}
                                        autoComplete="off"
                                    />
                                </div>
                                <div className="col-md-6 mb-3 d-flex flex-column">
                                    <label className="mb-1" htmlFor="secondaryPhone">
                                        {translatedText.SECONDARY_PHONE}
                                    </label>
                                    <MlxCountryPhoneInput
                                        className="custom-input-text p-inputtext-sm"
                                        id="secondaryPhone"
                                        defaultCountry="US"
                                        value={venueFormikForm?.values?.secondaryPhone}
                                        onChange={(value) => {
                                            venueFormikForm.setFieldTouched('secondaryPhone', true);
                                            venueFormikForm.setFieldValue('secondaryPhone', value, true);
                                        }}
                                        onBlur={() => {
                                            venueFormikForm.setFieldTouched('phoneNumber', true);
                                        }}
                                        autoComplete="off"
                                    />
                                </div>
                            </div>

                            <div className="mb-3 mt-2 d-flex flex-column">
                                <MlxGoogleMaps addressCallback={addressCallback} address={locationFormData} style={{ height: '300px' }} />
                            </div>

                            <div className="mb-3 d-flex flex-column">
                                <div className="d-flex justify-content-between">
                                    <label className="mb-1" htmlFor="address">
                                        {translatedText.CONTACT_ADDRESS}
                                    </label>
                                </div>
                                <InputText
                                    id="address"
                                    name="address"
                                    value={venueFormikForm.values.address}
                                    onChange={venueFormikForm.handleChange}
                                    onBlur={venueFormikForm.handleBlur}
                                    autoComplete="off"
                                />
                            </div>

                            <div className="row">
                                <div className="col-md-6 mb-3 d-flex flex-column">
                                    <label className="mb-1" htmlFor="zip">
                                        {translatedText.ZIP}
                                    </label>
                                    <InputText
                                        id="zip"
                                        name="zip"
                                        className="custom-input-text p-inputtext-sm"
                                        value={venueFormikForm.values.zip}
                                        onChange={venueFormikForm.handleChange}
                                        onBlur={venueFormikForm.handleBlur}
                                        autoComplete="off"
                                    />
                                </div>
                                <div className="col-md-6 mb-3 d-flex flex-column">
                                    <label className="mb-1" htmlFor="city">
                                        {translatedText.CITY}
                                    </label>
                                    <InputText
                                        id="city"
                                        name="city"
                                        className="custom-input-text p-inputtext-sm"
                                        value={venueFormikForm.values.city}
                                        onChange={venueFormikForm.handleChange}
                                        onBlur={venueFormikForm.handleBlur}
                                        autoComplete="off"
                                    />
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-6 mb-3 d-flex flex-column">
                                    <label className="mb-1" htmlFor="state">
                                        {translatedText.STATE}
                                    </label>
                                    <InputText
                                        id="state"
                                        name="state"
                                        className="custom-input-text p-inputtext-sm"
                                        value={venueFormikForm.values.state}
                                        onChange={venueFormikForm.handleChange}
                                        onBlur={venueFormikForm.handleBlur}
                                        autoComplete="off"
                                    />
                                </div>
                                <div className="col-md-6 mb-3 d-flex flex-column">
                                    <label className="mb-1" htmlFor="country">
                                        {translatedText.COUNTRY}
                                    </label>
                                    <InputText
                                        id="country"
                                        name="country"
                                        className="custom-input-text p-inputtext-sm"
                                        value={venueFormikForm.values.country}
                                        onChange={venueFormikForm.handleChange}
                                        onBlur={venueFormikForm.handleBlur}
                                        autoComplete="off"
                                    />
                                </div>
                            </div>

                            {isVenueEdit ? (
                                <div className="row">
                                    <div className="col-md-6 mb-3 d-flex flex-column">
                                        <label className="mb-1" htmlFor="preferredUrl">
                                            {translatedText.VENUE_URL}
                                        </label>
                                        <InputText
                                            id="preferredUrl"
                                            name="preferredUrl"
                                            className="custom-input-text p-inputtext-sm overflow-ellipsis"
                                            value={`${venueFormikForm.values.domainName}${domainSuffix}`}
                                            autoComplete="off"
                                            readOnly
                                            disabled
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className="step2-container-1 mb-4">
                                    <div className="venue-url-container rounded">
                                        <div className="mt-2 note d-flex flex-row align-items-center">
                                            <i className="pi pi-info-circle" />
                                            <span className="ms-1">{translatedText.CREATE_FIRST_AVENUE_WARNING}</span>
                                        </div>
                                        <div className="form-field d-flex flex-column mt-2">
                                            <label htmlFor="venue-url">{translatedText.PREFERRED_VENUE_URL}</label>
                                            <div className="mt-1 d-flex flex-row align-items-center">
                                                <InputText
                                                    className={`custom-input-text p-inputtext-sm ${venueFormikForm?.touched?.domainName && venueFormikForm?.errors?.domainName ? 'border-danger' : ''} ${domainRefAddVenue?.isAvailable && !venueFormikForm?.errors?.domainName ? 'text-success border-success' : ''}`}
                                                    autoComplete="off"
                                                    value={domainName}
                                                    onChange={(event) => setDomainName(event?.target?.value?.trim()?.toLowerCase())}
                                                />
                                                <span
                                                    className={`ms-1 ${domainRefAddVenue?.isAvailable && !venueFormikForm?.errors?.domainName ? 'text-success' : 'text-secondary'}`}
                                                >
                                                    {domainSuffix}{' '}
                                                    {venueFormikForm.values.domainName !== '' && domainRefAddVenue?.isAvailable && (
                                                        <i className="text-success pi pi-verified"></i>
                                                    )}
                                                </span>
                                            </div>
                                        </div>
                                        {venueFormikForm.touched.domainName && venueFormikForm.errors.domainName && (
                                            <div className="text-danger">{venueFormikForm.errors.domainName}</div>
                                        )}
                                        <div
                                            className="mt-2 d-flex flex-row align-items-center text-primary"
                                            style={{
                                                visibility: domainRefAddVenue?.isProgress ? 'visible' : 'hidden',
                                            }}
                                        >
                                            <i className="pi pi-spin pi-spinner" />
                                            <span className="ms-1">{translatedText.CHECKING_DOMAIN_AVAILABILITY}</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </form>

                        <div className="mlx-sidebar-footer">
                            <MlxButton
                                label={translatedText.CANCEL}
                                onClick={() => {
                                    onSidebarClose();
                                }}
                                icon="ri-close-large-line"
                                iconPos="right"
                                size="small"
                                outlined
                                type="button"
                            />
                            <MlxButton
                                className="ms-2"
                                label={isVenueEdit ? translatedText.UPDATE : translatedText.SAVE}
                                onClick={() => {
                                    venueFormikForm.handleSubmit();
                                }}
                                icon="ri-check-line"
                                iconPos="right"
                                size="small"
                                type="button"
                                loading={addVenueMutation.isLoading}
                                disabled={addVenueMutation.isLoading}
                            />
                        </div>
                    </>
                )}
            />
        </>
    );
};

export default AddVenueSidebar;
